import { lazy, Suspense, useEffect, useState } from 'react';
import { Ellipsis } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { useAppStore } from '../../app/store';
import { styleEntry, styleName } from '../../data/styles';
import type { Kit, Mark, Pattern } from '../../data/types';
import { STEP_COUNT, doneCount, nextStep, stepNumber } from '../../data/wizardSteps';
import { getKit } from '../../db/repos/kits';
import { listMarks } from '../../db/repos/marks';
import { getProjectPattern, openProjectPattern } from '../../db/repos/projects';
import { BPM_MAX, BPM_MIN } from '../../lib/grooveModel';
import { markMeta } from '../../lib/markMeta';
import { IconButton } from '../../ui/Button';
import { Readout } from '../../ui/Readout';
import { Row, RowList } from '../../ui/Row';
import { EmptyState, Progress } from '../../ui/States';
import { StepperSheet } from '../../ui/StepperSheet';
import screen from '../screen.module.css';
import { datesLine, dayLabel, firstLine, keyLabel } from './format';
import { KeySheet } from './KeySheet';
import { NotesSheet } from './NotesSheet';
import { ProjectMoreSheet } from './ProjectMoreSheet';
import { useProject, useProjects } from './projectsStore';
import type { WizardView } from './wizard/WizardSheets';
import styles from './Beat.module.css';

// Lazy: the step detail pulls in the style content and the wizard text (CLAUDE.md performance)
const WizardSheets = lazy(() => import('./wizard/WizardSheets'));
// Harmony (SPEC F7, F14) carries the harmony logic and Chord match, so it loads only when there is a key to read
const HarmonySheet = lazy(() => import('../harmony/HarmonySheet'));

type Sheet = 'bpm' | 'key' | 'notes' | 'more' | undefined;

/** Beat › Project home (SPEC F7, DESIGN_SYSTEM §4): the beat you are making. No primary button: the rows are the actions. */
export function ProjectHome() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const loaded = useProjects((s) => s.projects !== undefined);
  const { open, setBpm, setKey, setNotes } = useProjects();
  const activeId = useAppStore((s) => s.settings.activeProjectId);
  const [now] = useState(() => Date.now());
  const [sheet, setSheet] = useState<Sheet>();
  const [wizard, setWizard] = useState<WizardView>();
  const [kit, setKit] = useState<Kit>();
  const [pattern, setPattern] = useState<Pattern>();
  const [marks, setMarks] = useState<Mark[]>();
  const [harmony, setHarmony] = useState<{ sounds: number; tuning: number }>();
  const [harmonyOpen, setHarmonyOpen] = useState(false);

  useEffect(() => { void useProjects.getState().load(); }, []);

  // Opening a project makes it the active one (a URL or the list); the style only moves when the active project changes
  useEffect(() => {
    if (project && !project.archived && activeId !== project.id) open(project.id);
  }, [project, activeId, open]);

  // What the rows summarise. Refetched when the project changes (its updatedAt moves on every edit).
  const stamp = project?.updatedAt;
  const kitId = project?.kitId;
  useEffect(() => {
    if (!project) return;
    let live = true;
    void (kitId ? getKit(kitId) : Promise.resolve(undefined)).then((k) => live && setKit(k), () => undefined);
    void getProjectPattern(project).then((p) => live && setPattern(p), () => undefined);
    void listMarks().then((all) => live && setMarks(all.filter((m) => m.projectId === project.id)), () => undefined);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, stamp, kitId]);

  // The Harmony row: only with a key and at least one keyed sound (SPEC F7), so there is no placeholder row
  const keyRoot = project?.keyRoot;
  const minorKey = project?.minor;
  const styleOfProject = project?.styleId;
  useEffect(() => {
    // Without a key the row is hidden by `key` below, so there is nothing to clear here
    if (keyRoot === undefined || !styleOfProject || marks === undefined) return;
    let live = true;
    void import('../harmony/entries')
      .then((m) => m.harmonyEntries({ keyRoot, minor: minorKey, styleId: styleOfProject }, marks, kit))
      .then((entries) => live && setHarmony(entries.length ? { sounds: entries.length, tuning: entries.filter((e) => !e.fit.fits).length } : undefined))
      .catch(() => live && setHarmony(undefined));
    return () => { live = false; };
  }, [keyRoot, minorKey, styleOfProject, marks, kit]);

  if (!loaded) return <div className={screen.screen}><h1 className="visually-hidden">Project</h1><Progress active label="Loading project" /></div>;
  if (!project) {
    return (
      <div className={screen.screen}>
        <h1 className="t-title">Project not found</h1>
        <EmptyState message="This project isn't on this device. It may have been deleted." action={{ label: 'All projects', to: '/beat' }} />
      </div>
    );
  }

  const done = doneCount(project.checks);
  const next = nextStep(project.checks);
  const allDone = !next;
  const pct = Math.round((done / STEP_COUNT) * 100);
  const entry = styleEntry(project.styleId);
  const swing = pattern?.swing ?? entry?.swingDefault ?? 50;
  const latest = marks?.[0];
  const key = keyLabel(project);
  const notesLine = firstLine(project.notes);
  const filledPads = kit?.pads.filter((p) => p.sampleId).length ?? 0;

  const openGroove = async () => {
    await openProjectPattern(project.id);
    navigate(`/drums/groove?project=${project.id}`);
  };

  return (
    <div className={screen.screen}>
      <div className={styles.titleRow}>
        <h1 className="t-title">{project.name}</h1>
        <IconButton icon={Ellipsis} aria-label="More for this project" variant="tertiary" onClick={() => setSheet('more')} />
      </div>
      <p className={`t-body ${styles.meta}`}>
        {styleName(project.styleId)}, {datesLine(project, now)}
      </p>
      {project.finishedAt !== undefined && <p className={`t-meta ${styles.finished}`}>Finished {dayLabel(project.finishedAt, now)}</p>}

      <div className={styles.readouts}>
        <Readout label="BPM" value={String(project.bpm)} onClick={() => setSheet('bpm')} actionLabel="change" />
        <Readout label="Key" value={key ?? 'Set key'} kind="word" onClick={() => setSheet('key')} actionLabel="change" />
      </div>

      <div className={styles.progressHead}>
        <span className="t-label">{allDone ? 'All steps done' : `Step ${stepNumber(project.checks)} of ${STEP_COUNT}`}</span>
        <span className={`t-body ${styles.done}`}>{done} done</span>
      </div>
      <div
        className={styles.bar}
        role="progressbar"
        aria-label="Wizard progress"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={STEP_COUNT}
      >
        <span className={styles.fill} style={{ width: `${pct}%` }} />
      </div>

      <div className={styles.rows}>
        <RowList label="Project">
          <li>
            <Row
              large
              chevron="accent"
              title={next ? next.title : `All ${STEP_COUNT} steps done`}
              meta={next ? 'Next step, on your MPC' : 'Review the steps'}
              onClick={() => setWizard(next ? { view: 'step', stepId: next.id } : { view: 'all' })}
            />
          </li>
          <li>
            {kit ? (
              <Row title={kit.name} meta={`Kit, ${filledPads} of 16 pads`} chevron to={`/drums/kit?kit=${kit.id}&project=${project.id}`} />
            ) : (
              <Row title="No kit yet" meta="Kit, suggest one from your library" chevron to={`/drums/kit?project=${project.id}`} />
            )}
          </li>
          <li>
            <Row
              title={`${entry?.name ?? styleName(project.styleId)} groove`}
              meta={`Groove, ${pattern?.bpm ?? project.bpm} BPM, swing ${swing}%`}
              chevron
              onClick={() => void openGroove()}
            />
          </li>
          <li>
            {latest ? (
              <Row
                title={`${marks?.length ?? 0} ${marks?.length === 1 ? 'mark' : 'marks'}`}
                meta={markMeta(latest)}
                chevron
                to={`/dig/marks?project=${project.id}`}
              />
            ) : (
              <Row title="No marks yet" meta="Marks, save one while you dig" chevron to="/dig" />
            )}
          </li>
          {harmony && key && (
            <li>
              <Row
                title="Harmony"
                meta={`${harmony.sounds} ${harmony.sounds === 1 ? 'sound' : 'sounds'} in ${key}, ${harmony.tuning === 0 ? 'all fit' : `${harmony.tuning} ${harmony.tuning === 1 ? 'needs' : 'need'} tuning`}`}
                chevron
                onClick={() => setHarmonyOpen(true)}
              />
            </li>
          )}
          <li><Row title="Notes" meta={notesLine || 'Add notes'} chevron onClick={() => setSheet('notes')} /></li>
          <li><Row title="Fix a problem" meta="Learn, Fixes" chevron to="/learn/fixes" /></li>
        </RowList>
      </div>

      <StepperSheet
        open={sheet === 'bpm'}
        title="BPM"
        value={project.bpm}
        min={BPM_MIN}
        max={BPM_MAX}
        onChange={(v) => void setBpm(project.id, Math.round(v))}
        onClose={() => setSheet(undefined)}
      />
      <KeySheet
        open={sheet === 'key'}
        root={project.keyRoot}
        minor={project.minor}
        onChange={(k) => void setKey(project.id, k)}
        onClose={() => setSheet(undefined)}
      />
      <ProjectMoreSheet open={sheet === 'more'} project={project} onClose={() => setSheet(undefined)} />
      <NotesSheet open={sheet === 'notes'} notes={project.notes} onSave={(n) => void setNotes(project.id, n)} onClose={() => setSheet(undefined)} />

      {harmonyOpen && harmony && (
        <Suspense fallback={null}>
          <HarmonySheet project={project} marks={marks ?? []} kit={kit} onClose={() => setHarmonyOpen(false)} />
        </Suspense>
      )}

      {wizard && (
        <Suspense fallback={null}>
          <WizardSheets project={project} view={wizard} onView={setWizard} />
        </Suspense>
      )}
    </div>
  );
}
